import './DateInputField.scss';
import cn from 'classnames';
import * as Dfns from 'date-fns/fp';
import { ChangeEvent, Dispatch, ReactNode, SetStateAction, useCallback, useState } from 'react';
import { defaultIsDayOrRangeInvalid } from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { MAX_DATE, PRETTY_DATE_FORMAT } from '../DatePickerButton/DatePickerConstants';

const makePrettyDate = (date?: string) =>
  date ? Dfns.format(PRETTY_DATE_FORMAT)(Dfns.parseISO(date)) : '';

const checkMonth = (month: string) => {
  const parsedMonth = Number(month);
  if (month.length === 1) {
    return parsedMonth <= 1;
  }
  return parsedMonth > 0 && parsedMonth <= 12;
};

const checkDay = (month: string, day: string) => {
  const parsedMonth = Number(month);
  const parsedDay = Number(day);
  if (day.length === 1) {
    return parsedDay <= MAX_DATE[parsedMonth] / 10;
  }
  return parsedDay <= MAX_DATE[parsedMonth];
};

const validDate = (value: string) => {
  const parseDate = Dfns.parse(new Date(), PRETTY_DATE_FORMAT);
  const result = parseDate(value);
  return Dfns.isValid(result);
};

interface DateInputFieldProps {
  label: ReactNode;
  value: string;
  setValue?: Dispatch<SetStateAction<string>> | ((value: string) => void);
  customValidation?: {
    validate: (value: string) => boolean;
    errorMessage: string;
  }[];
  infoMessage?: string;
  className?: string;
  invalidateDateOrRange?: (date: string) => boolean;
  valueDateFormat?: string;
  dashBoarder?: boolean;
  clearPreset?: () => void;
  allowAnyDate?: boolean;
}

export const DateInputField = ({
  label,
  value,
  setValue,
  customValidation,
  infoMessage,
  className = '',
  invalidateDateOrRange = defaultIsDayOrRangeInvalid,
  valueDateFormat = 'yyyy-MM-dd',
  dashBoarder = false,
  clearPreset,
  allowAnyDate = false
}: DateInputFieldProps) => {
  const [prevValue, setPrevValue] = useState(value);
  const [tempDateStr, setTempDateStr] = useState(makePrettyDate(value));
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  // Sync tempDateStr when value is reassigned by parent (derived state during render)
  if (prevValue !== value) {
    setPrevValue(value);
    setTempDateStr(makePrettyDate(value));
  }

  const checkIsValid = useCallback((dateStr: string) => {
    if (validDate(dateStr)) {
      return true;
    }
    if (dateStr.length === 1) {
      return checkMonth(dateStr) && dateStr.match(/^[0-9]+$/) !== null;
    }
    if (dateStr.length === 2) {
      return checkMonth(dateStr) && dateStr.match(/^[0-9][0-9]+$/) !== null;
    }
    if (dateStr.length === 3) {
      return checkMonth(dateStr.substring(0, 2)) && dateStr.match(/^[0-9][0-9][/]+$/) !== null;
    }
    if (dateStr.length === 4) {
      const month = dateStr.substring(0, 2);
      const day = dateStr.substring(3);
      return (
        checkMonth(month) && checkDay(month, day) && dateStr.match(/^[0-9][0-9][/][0-9]+$/) !== null
      );
    }
    if (dateStr.length === 5) {
      const month = dateStr.substring(0, 2);
      const day = dateStr.substring(3);
      return (
        checkMonth(month) &&
        checkDay(month, day) &&
        dateStr.match(/^[0-9][0-9][/][0-9][0-9]+$/) !== null
      );
    }
    if (dateStr.length === 6) {
      const month = dateStr.substring(0, 2);
      const day = dateStr.substring(3, 5);
      return (
        checkMonth(month) &&
        checkDay(month, day) &&
        dateStr.match(/^[0-9][0-9][/][0-9][0-9][/]+$/) !== null
      );
    }
    if (dateStr.length === 7) {
      const month = dateStr.substring(0, 2);
      const day = dateStr.substring(3, 5);
      return (
        checkMonth(month) &&
        checkDay(month, day) &&
        dateStr.match(/^[0-9][0-9][/][0-9][0-9][/][0-9]+$/) !== null
      );
    }
    if (dateStr.length === 8) {
      const month = dateStr.substring(0, 2);
      const day = dateStr.substring(3, 5);
      return (
        checkMonth(month) &&
        checkDay(month, day) &&
        dateStr.match(/^[0-9][0-9][/][0-9][0-9][/][0-9][0-9]+$/) !== null
      );
    }
    if (dateStr.length === 9) {
      const month = dateStr.substring(0, 2);
      const day = dateStr.substring(3, 5);
      return (
        checkMonth(month) &&
        checkDay(month, day) &&
        dateStr.match(/^[0-9][0-9][/][0-9][0-9][/][0-9][0-9][0-9]+$/) !== null
      );
    }
    if (dateStr.length === 10) {
      const month = dateStr.substring(0, 2);
      const day = dateStr.substring(3, 5);
      return (
        checkMonth(month) &&
        checkDay(month, day) &&
        dateStr.match(/^[0-9][0-9][/][0-9][0-9][/][0-9][0-9][0-9][0-9]+$/) !== null
      );
    }
    if (dateStr.length > 10) {
      return false;
    }
    return true;
  }, []);

  let customErrorMessage = '';
  if (customValidation && value.length > 0 && isValid) {
    customValidation.forEach(({ validate, errorMessage: validationError }) => {
      if (!customErrorMessage && !validate(value)) {
        customErrorMessage = validationError;
      }
    });
  }

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      let newValue = event.target.value;
      if (newValue.length > 10) {
        return;
      }
      const isAdding = newValue.length > tempDateStr.length;
      if (
        isAdding &&
        ((newValue.length === 2 && newValue.match(/^[0-9][0-9]$/) !== null) ||
          (newValue.length === 5 && newValue.match(/^[0-9][0-9][/][0-9][0-9]$/) !== null))
      ) {
        newValue += '/';
      }
      setTempDateStr(newValue);
      const valid = checkIsValid(newValue);
      if (!valid) {
        setErrorMessage('Invalid date format');
        setIsValid(false);
        return;
      }

      if (setValue && newValue.length === 10) {
        const parseDate = Dfns.parse(new Date(), 'MM/dd/yyyy');
        const result = parseDate(newValue);

        if (!Dfns.isValid(result)) {
          setErrorMessage('Invalid date format');
          setIsValid(false);
          return;
        }
        const formattedDate = Dfns.format(valueDateFormat, result);
        if (!allowAnyDate && invalidateDateOrRange(formattedDate)) {
          setErrorMessage('Outside date range');
          setIsValid(false);
        } else {
          setValue(formattedDate);
          setIsValid(true);
          if (clearPreset) {
            clearPreset();
          }
        }
      } else {
        setIsValid(true);
      }
    },
    [
      setValue,
      tempDateStr,
      invalidateDateOrRange,
      valueDateFormat,
      checkIsValid,
      clearPreset,
      allowAnyDate
    ]
  );

  return (
    <div className={cn('date-input-field-container', className)}>
      <div className='date-input-field-value-container'>
        {label && <div className='date-input-field-label'>{label}</div>}
        <input
          className={cn('date-input-field-value', {
            invalid: !isValid,
            dashed: dashBoarder
          })}
          type='text'
          placeholder='mm/dd/yyyy'
          value={tempDateStr}
          onChange={onChange}
          onFocus={e => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            setTempDateStr('');
          }}
          onBlur={e => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            const prettyDate = makePrettyDate(value);
            const valid = checkIsValid(prettyDate);
            setIsValid(valid);
            setErrorMessage('');
            setTempDateStr(prettyDate);
          }}
        />
      </div>
      {infoMessage && <div className='date-input-field-info'>{infoMessage}</div>}
      {!isValid && <div className='date-input-field-error'>{errorMessage}</div>}
      {customErrorMessage && <div className='date-input-field-error'>{customErrorMessage}</div>}
    </div>
  );
};
